// client/src/components/common/PickerPresets.tsx
//
// Quick ranges (Today, Last 7 days …) in the range picker (ST-00204, ST-00294): in the phone
// sheet one horizontally scrolling row of large chips; in the desktop popup a vertical list down
// its left side. The one matching the pending range is pressed.

import { useEffect, useRef } from 'react';
import { usePickerVariant } from './PickerSurface';
import { presetMatches, type RangePreset } from './pickerLogic';

export interface PickerPresetsProps {
  presets: RangePreset[];
  start: Date | null;
  end: Date | null;
  onPick: (preset: RangePreset) => void;
  label?: string;
}

export default function PickerPresets({ presets, start, end, onPick, label = 'Quick ranges' }: PickerPresetsProps) {
  const list = usePickerVariant() === 'popover';
  const row = useRef<HTMLDivElement | null>(null);

  // Bring the pressed chip into view when the sheet opens, if it sits past the visible part.
  useEffect(() => {
    const el = row.current;
    if (list) return;
    const pressed = el?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (!el || !pressed || typeof el.scrollTo !== 'function') return;
    if (pressed.offsetLeft + pressed.offsetWidth > el.clientWidth) {
      el.scrollTo({ left: Math.max(0, pressed.offsetLeft - 16) });
    }
    // Only on open: later taps are made on chips already in view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!presets.length) return null;
  return (
    <div ref={row} className={list ? 'picker-presets' : 'picker-chips'} role="group" aria-label={label}>
      {presets.map((p) => (
        <button
          key={p.id}
          type="button"
          className={list ? 'picker-preset' : 'picker-chip'}
          data-preset={p.id}
          aria-pressed={presetMatches(p, start, end)}
          onClick={() => onPick(p)}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}
